
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import _ from "lodash";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatDurationWithLabel } from "../utils/format";
import TruncateWithTooltip from "src/components/truncateWithTooltip";
import styles from "./common.module.scss";

const usePrimaryColor = () =>
  useSelector((state: any) => _.get(state.accounts, "item.theme.primaryColor") || "#e51058");


const PortalChartTooltip: React.FC<{
  anchor: { x: number; y: number } | null;
  children: React.ReactNode;
}> = ({ anchor, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !anchor) {
      setPos(null);
      return;
    }
    const margin = 8;
    const { offsetWidth: w, offsetHeight: h } = el;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = anchor.x - w / 2;
    left = Math.max(margin, Math.min(left, vw - w - margin));

    let top = anchor.y - h - margin;
    if (top < margin) top = anchor.y + margin;
    top = Math.min(top, vh - h - margin);

    setPos({ left, top });
  }, [anchor?.x, anchor?.y]);

  if (!anchor) return null;

  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        zIndex: 1000,
        pointerEvents: "none",
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

const useChartPortalTooltip = (
  // eslint-disable-next-line no-unused-vars
  renderContent: (item: any) => React.ReactNode,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<{ item: any; anchor: { x: number; y: number } } | null>(
    null,
  );

  const onMouseMove = (state: any) => {
    const payload = state?.activePayload;
    const coordinate = state?.activeCoordinate;
    if (!state?.isTooltipActive || !payload || !payload.length || !coordinate) {
      setActive((prev) => (prev ? null : prev));
      return;
    }
    const rect = containerRef.current?.getBoundingClientRect();
    const anchor = rect
      ? { x: rect.left + coordinate.x, y: rect.top + coordinate.y }
      : { x: coordinate.x, y: coordinate.y };
    const item = payload[0].payload;
    setActive((prev) =>
      prev && prev.item === item && prev.anchor.x === anchor.x && prev.anchor.y === anchor.y
        ? prev
        : { item, anchor },
    );
  };

  const onMouseLeave = () => setActive(null);

  const tooltipPortal = (
    <PortalChartTooltip anchor={active?.anchor ?? null}>
      {active ? renderContent(active.item) : null}
    </PortalChartTooltip>
  );

  return { containerRef, onMouseMove, onMouseLeave, tooltipPortal };
};

export const TrendsLineChart: React.FC<{ data?: any[]; dataKey?: string; height?: number }> = ({
  data = [],
  dataKey = "value",
  height = 90,
}) => {
  const primaryColor = usePrimaryColor();
  const gradientId = React.useId();
  const domain: [number, number] | undefined = data.every(
    (point) => Number(_.get(point, dataKey, 0)) < 4,
  )
    ? [0, 4]
    : undefined;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={primaryColor} stopOpacity={0.35} />
            <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <YAxis hide domain={domain} />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={primaryColor}
          strokeWidth={1}
          fill={`url(#${gradientId})`}
          dot={data.length === 1}
          activeDot
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export const TrendBarChart: React.FC<{ data?: any[]; dataKey?: string; width?: number }> = ({
  data = [],
  dataKey = "views",
  width = 60,
}) => {
  const primaryColor = usePrimaryColor();
  const [focusBar, setFocusBar] = useState<number | null>(null);

  const { containerRef, onMouseMove, onMouseLeave, tooltipPortal } = useChartPortalTooltip(
    (item: any) => {
      const fallback =
        dataKey === "views" ? 0 : formatDurationWithLabel(_.get(item, "totalTime", 0)).value;
      const thumbnail = _.get(item, "thumbnail", null);
      return (
        <div className={styles.trendTooltip}>
          <TruncateWithTooltip
            className={styles.trendTooltipTitle}
            text={String(_.get(item, "name") || `#${_.get(item, "index", "")}`)}
          />
          {thumbnail && (
            <img className={styles.trendTooltipThumb} src={thumbnail} alt="" height={80} width={80} />
          )}
          <p className={styles.trendTooltipValue}>{_.get(item, "views") || fallback}</p>
        </div>
      );
    },
  );

  return (
    <div ref={containerRef}>
      {tooltipPortal}
      <ResponsiveContainer width={width} height={50}>
        <BarChart
          data={data}
          onMouseMove={(state: any) => {
            setFocusBar(state?.isTooltipActive ? state.activeTooltipIndex : null);
            onMouseMove(state);
          }}
          onMouseLeave={() => {
            setFocusBar(null);
            onMouseLeave();
          }}
        >
          <Tooltip cursor={{ fill: "#F5F5F5" }} content={() => null} />
          <Bar minPointSize={3} dataKey={dataKey} radius={[2, 2, 0, 0]}>
            {data.map((_entry, index) => (
              <Cell key={index} fill={focusBar === index ? primaryColor : "#D5D5D5"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const TrendsAreaChart: React.FC<{
  data?: any[];
  height?: number;
  width?: number | string;
}> = ({ data = [], height = 50, width = "100%" }) => {
  const primaryColor = usePrimaryColor();
  const gradientId = React.useId();

  const { containerRef, onMouseMove, onMouseLeave, tooltipPortal } = useChartPortalTooltip(
    (item: any) => (
      <div className={styles.trendTooltip}>
        <p className={styles.trendTooltipTitle}>Views: {_.get(item, "count", "")}</p>
        <p className={styles.trendTooltipValue}>
          {formatDurationWithLabel(_.get(item, "currentTime", 0)).value}
        </p>
      </div>
    ),
  );

  if (!data || data.length < 1) return null;

  return (
    <div ref={containerRef}>
      {tooltipPortal}
      <ResponsiveContainer width={width} height={height}>
        <AreaChart
          data={data}
          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={primaryColor} stopOpacity={0.35} />
              <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis hide dataKey="index" />
          <YAxis hide dataKey="count" />
          <Tooltip cursor={{ fill: "#F5F5F5" }} content={() => null} />
          <Area
            type="monotone"
            dataKey="count"
            stroke={primaryColor}
            strokeWidth={1}
            fill={`url(#${gradientId})`}
            dot={data.length === 1 ? { fill: primaryColor, stroke: primaryColor, r: 4 } : false}
            activeDot={{ fill: primaryColor, stroke: primaryColor }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

